import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  useDisclosure,
  useToast,
  Box,
  Text,
} from "@chakra-ui/react";
import axios from "axios";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ChatState } from "../../Context/ChatProvider";
import { Ornament } from "../ui/Icons";
import CodeInput from "../ui/CodeInput";

// Восстановление пароля по коду из письма
const ForgotPasswordModal = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const history = useHistory();
  const { setUser } = ChatState();

  const [step, setStep] = useState("email"); // email → code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const oops = (error, title = "Не получилось") =>
    toast({
      title:
        error.response?.data?.message === "EMAIL_DISABLED"
          ? "Восстановление пароля пока недоступно"
          : title,
      description:
        error.response?.data?.message === "EMAIL_DISABLED"
          ? "Письма пока не отправляются. Попросите админа клуба выдать вам новый пароль"
          : error.response?.data?.message,
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "bottom",
    });

  const start = () => {
    setStep("email");
    setCode("");
    setPassword("");
    setResendIn(0);
    onOpen();
  };

  const sendCode = async () => {
    if (!email || !email.includes("@")) {
      toast({
        title: "Укажите почту",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post("/api/user/forgot-password", { email });
      setStep("code");
      setResendIn(60);
      toast({
        title: "Код отправлен",
        description: `Если такой аккаунт есть, письмо придёт на ${email}`,
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      oops(error, "Не удалось отправить код");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (code.length < 6) {
      toast({
        title: "Введите код из шести цифр",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    if (password.length < 6) {
      toast({
        title: "Пароль должен быть не короче 6 символов",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/api/user/reset-password", {
        email,
        code,
        password,
      });
      toast({
        title: "Пароль изменён",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      setUser(data);
      localStorage.setItem("userInfo", JSON.stringify(data));
      onClose();
      history.push("/chats");
    } catch (error) {
      oops(error, "Не удалось изменить пароль");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <span onClick={start}>{children}</span>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="rgba(20, 35, 23, 0.45)" />
        <ModalContent>
          <ModalHeader
            fontSize="24px"
            fontFamily="'Prata', serif"
            color="#243D27"
            d="flex"
            flexDir="column"
            alignItems="center"
            pb={2}
          >
            Восстановление пароля
            <Box mt={2}>
              <Ornament color="#8D414B" opacity={0.55} width={110} />
            </Box>
          </ModalHeader>
          <ModalCloseButton borderRadius="10px" />
          <ModalBody pb={6}>
            {step === "email" ? (
              <>
                <Text fontSize="sm" color="rgba(36, 61, 39, 0.7)" mb={3}>
                  Укажите почту, с которой вы вступали в клуб — пришлём код для
                  смены пароля.
                </Text>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    autoFocus
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendCode()}
                  />
                </FormControl>
                <Button
                  colorScheme="burgundy"
                  w="100%"
                  h="44px"
                  mt={4}
                  isLoading={loading}
                  onClick={sendCode}
                  _hover={{ bg: "#7A3841" }}
                >
                  Прислать код
                </Button>
              </>
            ) : (
              <>
                <Text
                  fontSize="sm"
                  color="rgba(36, 61, 39, 0.7)"
                  mb={3}
                  textAlign="center"
                >
                  Код отправлен на{" "}
                  <Text as="span" fontWeight={600} color="#243D27">
                    {email}
                  </Text>
                </Text>

                <CodeInput value={code} onChange={setCode} />

                <FormControl mt={4}>
                  <FormLabel color="#243D27" fontSize="sm">
                    Новый пароль
                  </FormLabel>
                  <InputGroup size="md">
                    <Input
                      type={show ? "text" : "password"}
                      placeholder="Не короче 6 символов"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && submit()}
                    />
                    <InputRightElement width="4.5rem">
                      <Button
                        h="1.75rem"
                        size="sm"
                        variant="ghost"
                        colorScheme="pine"
                        onClick={() => setShow(!show)}
                      >
                        {show ? "Скрыть" : "Показать"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  colorScheme="burgundy"
                  w="100%"
                  h="44px"
                  mt={4}
                  isLoading={loading}
                  onClick={submit}
                  _hover={{ bg: "#7A3841" }}
                >
                  Сменить пароль и войти
                </Button>

                <Box d="flex" justifyContent="space-between" mt={2}>
                  <Button
                    variant="link"
                    size="sm"
                    colorScheme="pine"
                    fontWeight={400}
                    onClick={() => setStep("email")}
                  >
                    Изменить почту
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    colorScheme="burgundy"
                    fontWeight={400}
                    isDisabled={resendIn > 0}
                    onClick={sendCode}
                  >
                    {resendIn > 0
                      ? `Отправить ещё раз (${resendIn})`
                      : "Отправить ещё раз"}
                  </Button>
                </Box>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ForgotPasswordModal;
